import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Icon } from "@/components/Icon";
import { CarTabIcon } from "@/components/CarImage";

import "./companion.css";

const TITLE = "Aplicația web — Asistent Auto";
const DESCRIPTION =
  "Tabloul de bord pe ecran mare pentru familii cu 3+ mașini, mici afaceri și service-uri. Aceleași date ca în aplicație, mai mult spațiu pe browser.";

type SideItem = {
  // Material Symbol; omitted for the Cars row, which uses the app's own
  // Cars-tab outline (`CarTabIcon`, design.md §8).
  icon?: string;
  label: string;
  active?: boolean;
  badge?: string;
};

const sideMain: SideItem[] = [
  { label: "Mașini", active: true, badge: "8" },
  { icon: "shield", label: "Polițe" },
  { icon: "event", label: "Calendar" },
  { icon: "folder", label: "Documente" },
];

const sideTeam: SideItem[] = [
  { icon: "groups", label: "Utilizatori" },
  { icon: "notifications", label: "Reminder-uri" },
  { icon: "download", label: "Export" },
  { icon: "settings", label: "Setări" },
];

const rows = [
  {
    plate: "B 101 RYA",
    name: "BMW X5",
    sub: "xDrive40i M Sport · 2023",
    when: "ITP · 2026",
    tone: "success" as const,
    pillIcon: "check_circle",
    pillLabel: "Activ",
  },
  {
    plate: "CJ 12 ELE",
    name: "Dacia Spring",
    sub: "Electric 65 · 2024",
    when: "ITP · 12 zile",
    tone: "warn" as const,
    pillIcon: "schedule",
    pillLabel: "Expiră curând",
  },
  {
    plate: "B 33 GTI",
    name: "VW Golf 8",
    sub: "GTI Performance · 2022",
    when: "RCA · −3 zile",
    tone: "error" as const,
    pillIcon: "error",
    pillLabel: "Expirat",
  },
  {
    plate: "B 22 ANA",
    name: "Audi Q3",
    sub: "Sportback 35 TFSI · 2023",
    when: "ITP · 2027",
    tone: "success" as const,
    pillIcon: "check_circle",
    pillLabel: "Activ",
  },
  {
    plate: "CJ 88 PAR",
    name: "Toyota Yaris",
    sub: "Hybrid Style · 2022",
    when: "CASCO · 4 luni",
    tone: "success" as const,
    pillIcon: "check_circle",
    pillLabel: "Activ",
  },
];

const audience = [
  {
    icon: "family_restroom",
    title: "Familia cu 3+ mașini",
    body: "Tu, partenerul, părinții, copilul cu mașina cumpărată second hand. Toate într-un singur tablou — cine plătește RCA-ul lunii viitoare nu mai e mister.",
  },
  {
    icon: "store",
    title: "Mica afacere cu 5-15 mașini",
    body: "Restaurante, firme de transport mărfuri ușoare, agenții imobiliare cu flotă. Multi-user, roluri, export Excel direct pentru contabil.",
  },
  {
    icon: "handyman",
    title: "Service auto sau broker",
    body: "Importi mașinile clienților, le ții documentele, primești reminder înainte de termen. Tu suni, ei cred că-ţi pasă mai mult decât asigurătorul.",
  },
] as const;

export default function CompanionPage() {
  return (
    <>
      <Seo title={TITLE} description={DESCRIPTION} path="/app" />
      <header className="page-hero">
        <div className="shell">
          <div className="section-eyebrow">Aplicația web · Beta deschis</div>
          <h1>
            Aceleași date.
            <br />
            <em>Mai mult spațiu de respirat.</em>
          </h1>
          <p>
            Pentru când ai 3+ mașini, vrei să trimiți polița mecanicului sau să
            dai acces familiei. Aplicația web e parte din Asistent Auto —
            același cont, mai mult spațiu pe ecran.
          </p>
        </div>
      </header>

      <section className="section section--tight">
        <div className="shell">
          <div className="companion">
            <div className="companion-chrome">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
              <span className="url">app.asistentauto.ro/garaj</span>
            </div>
            <div className="companion-body">
              <aside className="companion-side">
                <div className="companion-side-brand">
                  <span className="nav-brand-mark">
                    <img src="/simple_icon.svg" alt="" width={30} height={20} />
                  </span>
                  Asistent Auto
                </div>
                <h6>Garaj</h6>
                <ul>
                  {sideMain.map((item) => (
                    <li key={item.label} className={item.active ? "active" : undefined}>
                      {item.icon ? <Icon name={item.icon} /> : <CarTabIcon />}
                      {item.label}
                      {item.badge ? (
                        <span className="companion-side-badge">{item.badge}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <h6>Echipă</h6>
                <ul>
                  {sideTeam.map((item) => (
                    <li key={item.label}>
                      {item.icon ? <Icon name={item.icon} /> : <CarTabIcon />}
                      {item.label}
                    </li>
                  ))}
                </ul>
              </aside>

              <main className="companion-main">
                <div className="companion-main-head">
                  <h3>Garaj — 8 mașini</h3>
                  <div className="companion-main-actions">
                    <button className="btn btn--secondary" type="button">
                      <Icon name="file_download" />
                      Export
                    </button>
                    <button className="btn btn--primary" type="button">
                      <Icon name="add" />
                      Adaugă
                    </button>
                  </div>
                </div>

                <div className="stats">
                  <div className="companion-stat">
                    <div className="label">Toate</div>
                    <div className="value">8</div>
                  </div>
                  <div className="companion-stat">
                    <div className="label">Expiră curând</div>
                    <div className="value warn">2</div>
                  </div>
                  <div className="companion-stat">
                    <div className="label">Expirate</div>
                    <div className="value error">1</div>
                  </div>
                </div>

                <div className="companion-table">
                  {rows.map((row) => (
                    <div key={row.plate} className="companion-row">
                      <span className="plate">
                        <span className="mini-plate-ro">RO</span> {row.plate}
                      </span>
                      <div className="name">
                        {row.name}
                        <small>{row.sub}</small>
                      </div>
                      <span className="when">{row.when}</span>
                      <span className={`pill ${row.tone}`}>
                        <Icon name={row.pillIcon} />
                        {row.pillLabel}
                      </span>
                    </div>
                  ))}
                </div>
              </main>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shell">
          <div className="section-eyebrow">Pentru cine e</div>
          <h2 className="section-title">
            Trei moduri în care <em>aplicația te scapă</em> de Excel.
          </h2>

          <div className="bento">
            {audience.map((tile) => (
              <div key={tile.title} className="tile span-4">
                <div className="icon-box">
                  <Icon name={tile.icon} />
                </div>
                <h3>{tile.title}</h3>
                <p>{tile.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shell">
          <div className="cta-strip">
            <div style={{ position: "relative", zIndex: 1 }}>
              <h2>
                Vrei beta-ul? <em>Iți deschidem contul.</em>
              </h2>
              <p>
                Aplicația web e în beta deschis. Te conectezi cu același cont
                din aplicația mobilă Asistent Auto, datele se sincronizează în
                30 de secunde.
              </p>
              <Link to="/#download" className="btn btn--ghost-light btn--lg">
                <Icon name="download" />
                Descarcă aplicația, apoi intră aici
              </Link>
            </div>
            <div />
          </div>
        </div>
      </section>
    </>
  );
}
